import dayjs from "dayjs";
import { useMemo, useState } from "react";

import {
  IEditedAnswer,
  ISurveyQuestionForEdit,
} from "../../../@types/admin/AdminSurvey";
import { useUpdateSurveyAnswer } from "../../../hooks/mutations/admin/surveys";
import { useSurveyAnswerForEdit } from "../../../hooks/queries/admin/surveys";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminDateField,
  ErrorMessage,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  WarningNotice,
} from "../shared";
import {
  EditorActionButtons,
  EditorActions,
  OptionRow,
  QuestionCard,
  QuestionList,
  QuestionMeta,
  QuestionText,
  ReadOnlyAnswer,
} from "./AdminSurveys.styles";
import {
  buildAnswersPayload,
  getAnswerValue,
  getSelectedOptionIds,
  getVisibleQuestions,
} from "./surveyVisibility";

interface ISurveyAnswerEditorProps {
  surveyAnswerId: string;
  /** Lets the parent drill-down know whether it can navigate away. */
  onDirtyChange?: (isDirty: boolean) => void;
}

/** The editor's field value shape (`datetime-local`'s), in local time. */
const DATE_TIME_INPUT = "YYYY-MM-DDTHH:mm";

/**
 * Stored instant (UTC ISO from the API) → the field's local `YYYY-MM-DDTHH:mm`.
 * This used to slice the first 16 characters off the ISO string, which showed
 * the UTC digits as if they were local time.
 */
function toDateTimeInput(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_TIME_INPUT) : "";
}

/**
 * Field value → what the API is sent. The field's bare `YYYY-MM-DDTHH:mm` has
 * no offset, so .NET parsed it as Kind=Unspecified and Npgsql refused to write
 * it to a `timestamptz` column — every save with a date 500ed (API-2-476).
 * `toISOString` sends the same instant as UTC with a `Z`, which also means an
 * untouched date round-trips to exactly what was stored.
 */
function toApiDateTime(value: string): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
}

/**
 * Edits one submitted survey answer — Retool's `editSurveyAnswers_modalFrame`
 * plus its `container1` question renderer (EN-5164).
 *
 * ⚠️ **This writes a clinical record.** Two rules carry over from the export
 * and both matter:
 *
 * - Which questions appear comes from the ported `getVisibleQuestions` walk,
 *   not from the raw question list. Changing a branching answer re-routes the
 *   rest of the form as you type.
 * - The PUT sends every **visible** question, edited or not — an untouched one
 *   re-sends what it already had. Sending only the edits would read as
 *   clearing the rest, and sending a hidden question would write an answer the
 *   patient was never asked for.
 */
export function SurveyAnswerEditor({
  surveyAnswerId,
  onDirtyChange,
}: ISurveyAnswerEditorProps) {
  const { showToast } = useToast();
  const { surveyAnswer, isLoading, isError } =
    useSurveyAnswerForEdit(surveyAnswerId);
  const { updateSurveyAnswer, isUpdatingAnswer } = useUpdateSurveyAnswer();

  const [edits, setEdits] = useState<Record<string, IEditedAnswer>>({});
  const [answeredDate, setAnsweredDate] = useState<string | null>(null);

  const questions = surveyAnswer?.questions ?? [];
  const visibleQuestions = useMemo(
    () => getVisibleQuestions(questions, edits),
    [questions, edits]
  );

  const storedDate = toDateTimeInput(surveyAnswer?.answeredDate);

  // This writes a clinical record, so an edit must not save what the patient
  // could never have entered. Only edited questions are checked — stored
  // legacy answers may predate the current config, and an untouched question
  // must not block an unrelated fix.
  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    visibleQuestions.forEach((question) => {
      if (!(question.id in edits)) return;
      const label = question.text ?? "This question";
      const value = getAnswerValue(question, edits);
      const selectedIds = getSelectedOptionIds(question, edits);

      if (question.mandatory && !value && selectedIds.length === 0) {
        errors.push(`${label}: an answer is required.`);
        return;
      }

      if (
        (question.type === "Slider" || question.type === "SliderVas") &&
        value
      ) {
        const numeric = Number(value);
        const min = question.sliderConfig?.min;
        const max = question.sliderConfig?.max;
        if (Number.isNaN(numeric)) {
          errors.push(`${label}: the value must be a number.`);
        } else if (
          (min != null && numeric < min) ||
          (max != null && numeric > max)
        ) {
          errors.push(
            `${label}: the value must be between ${min ?? "the minimum"} and ${
              max ?? "the maximum"
            }.`
          );
        }
      }
    });
    return errors;
  }, [visibleQuestions, edits]);
  const dateValue = answeredDate ?? storedDate;
  const isDirty =
    Object.keys(edits).length > 0 ||
    (answeredDate !== null && answeredDate !== storedDate);

  const setDirty = (next: boolean) => onDirtyChange?.(next);

  const setAnswer = (questionId: string, answer: IEditedAnswer) => {
    setEdits((previous) => ({ ...previous, [questionId]: answer }));
    setDirty(true);
  };

  const handleReset = () => {
    setEdits({});
    setAnsweredDate(null);
    setDirty(false);
  };

  const handleSave = async () => {
    if (!surveyAnswer || validationErrors.length > 0) return;

    try {
      await updateSurveyAnswer({
        surveyAnswerId,
        request: {
          answeredDate: toApiDateTime(dateValue),
          answers: buildAnswersPayload(visibleQuestions, edits),
        },
      });
      showToast("The survey answers were updated successfully.", "success");
      handleReset();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating these survey answers."
        ),
        "warning"
      );
    }
  };

  if (isLoading) return <ErrorMessage>Loading the answers...</ErrorMessage>;
  if (isError || !surveyAnswer) {
    return (
      <ErrorMessage>
        Failed to load these survey answers. Close the dialog and try again.
      </ErrorMessage>
    );
  }

  const renderControl = (question: ISurveyQuestionForEdit) => {
    const value = getAnswerValue(question, edits);
    const selectedIds = getSelectedOptionIds(question, edits);
    const options = question.options ?? [];
    const controlId = `question-${question.id}`;

    switch (question.type) {
      case "SingleSelect":
        return options.map((option) => (
          <OptionRow key={option.id}>
            <input
              type="radio"
              name={controlId}
              checked={selectedIds.includes(option.id)}
              disabled={isUpdatingAnswer}
              onChange={() =>
                setAnswer(question.id, {
                  surveyQuestionOptionIds: [option.id],
                  answerValue: String(option.value),
                })
              }
            />
            {option.label ?? String(option.value)}
          </OptionRow>
        ));

      case "MultiSelect":
        return options.map((option) => (
          <OptionRow key={option.id}>
            <input
              type="checkbox"
              checked={selectedIds.includes(option.id)}
              disabled={isUpdatingAnswer}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...selectedIds, option.id]
                  : selectedIds.filter((id) => id !== option.id);
                setAnswer(question.id, {
                  surveyQuestionOptionIds: next,
                  answerValue: null,
                });
              }}
            />
            {option.label ?? String(option.value)}
          </OptionRow>
        ));

      case "Slider":
      case "SliderVas":
        return (
          <FilterInput
            id={controlId}
            type="number"
            min={question.sliderConfig?.min}
            max={question.sliderConfig?.max}
            step={question.sliderConfig?.step ?? 1}
            value={value}
            disabled={isUpdatingAnswer}
            onChange={(e) =>
              setAnswer(question.id, {
                answerValue: e.target.value,
                surveyQuestionOptionIds: null,
              })
            }
          />
        );

      case "Date":
        return (
          <AdminDateField
            id={controlId}
            value={value ? value.slice(0, 10) : ""}
            disabled={isUpdatingAnswer}
            onChange={(next) =>
              setAnswer(question.id, {
                answerValue: next,
                surveyQuestionOptionIds: null,
              })
            }
          />
        );

      case "Text":
      case "MultilineText":
        return (
          <FilterInput
            id={controlId}
            type="text"
            value={value}
            disabled={isUpdatingAnswer}
            onChange={(e) =>
              setAnswer(question.id, {
                answerValue: e.target.value,
                surveyQuestionOptionIds: null,
              })
            }
          />
        );

      default:
        // Media, Informative and FiveQuestionsAboutSupOx. Retool rendered no
        // control for these either; the stored value is shown so it is visible
        // rather than silently dropped, and it round-trips untouched.
        return (
          <ReadOnlyAnswer>
            {value || "No answer"} — this question type can&apos;t be edited
            here.
          </ReadOnlyAnswer>
        );
    }
  };

  return (
    <>
      {isDirty && (
        <WarningNotice>
          Unsaved changes to a submitted patient answer. Saving writes to the
          clinical record and is logged in this answer&apos;s history.
        </WarningNotice>
      )}

      <FilterGroup>
        <FilterLabel htmlFor="survey-answered-date">Answered date</FilterLabel>
        <AdminDateField
          id="survey-answered-date"
          withTime
          value={dateValue}
          disabled={isUpdatingAnswer}
          onChange={(next) => {
            setAnsweredDate(next);
            setDirty(true);
          }}
        />
      </FilterGroup>

      <QuestionList>
        {visibleQuestions.map((question) => (
          <QuestionCard key={question.id} edited={question.id in edits}>
            <QuestionText>{question.text ?? "(no question text)"}</QuestionText>
            <QuestionMeta>
              {question.type}
              {question.mandatory ? " · required" : ""}
            </QuestionMeta>
            {renderControl(question)}
          </QuestionCard>
        ))}
      </QuestionList>

      {visibleQuestions.length < questions.length && (
        <FieldHint>
          {questions.length - visibleQuestions.length} question
          {questions.length - visibleQuestions.length === 1
            ? " is"
            : "s are"}{" "}
          hidden by this survey&apos;s branching and will not be saved.
        </FieldHint>
      )}

      {validationErrors.length > 0 && (
        <ErrorMessage>
          {validationErrors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </ErrorMessage>
      )}

      <EditorActions>
        <FieldHint>
          {visibleQuestions.length} of {questions.length} questions shown
        </FieldHint>
        <EditorActionButtons>
          <FilterButton
            variant="secondary"
            disabled={isUpdatingAnswer || !isDirty}
            onClick={handleReset}
          >
            Reset
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={
              isUpdatingAnswer || !isDirty || validationErrors.length > 0
            }
            onClick={handleSave}
          >
            {isUpdatingAnswer ? "Saving..." : "Save answers"}
          </FilterButton>
        </EditorActionButtons>
      </EditorActions>
    </>
  );
}
